/**
 * Schema Converter — Converts a parsed schema model between SQL dialects.
 *
 * Internal conversion pipeline:
 *   1. Dedup columns (remove duplicate column definitions per table)
 *   2. Dedup PK columns (remove duplicate column refs in PRIMARY KEY)
 *   3. Fix types (VARCHAR(255) → PIC length, bad DECIMAL → PIC-based fix)
 *   4. Dialect convert (identifier quoting, type remaps, function remaps)
 *   5. Validate (check for remaining issues, build action items)
 *
 * Input: parsed schema from schemaParser.js
 * Output: converted schema + assessment report
 */

import { parsePicComment } from './schemaParser'

// ---------------------------------------------------------------------------
// Step 1: Dedup columns — remove duplicate column definitions per table
// ---------------------------------------------------------------------------
function dedupColumns(tables) {
  const changes = []

  const dedupedTables = tables.map(table => {
    const seen = new Map()  // colName (lowercase) → first index
    const dedupedCols = []
    let removed = 0

    for (const col of table.columns) {
      const key = col.name.toLowerCase()
      if (seen.has(key)) {
        removed++
      } else {
        seen.set(key, dedupedCols.length)
        dedupedCols.push({ ...col })
      }
    }

    if (removed > 0) {
      changes.push({
        type: 'dedup_columns',
        table: table.name,
        before: table.columns.length,
        after: dedupedCols.length,
        removed,
        message: `${table.name}: ${table.columns.length} → ${dedupedCols.length} columns (removed ${removed} duplicates)`,
      })
    }

    return { ...table, columns: dedupedCols }
  })

  return { tables: dedupedTables, changes }
}

// ---------------------------------------------------------------------------
// Step 2: Dedup PK columns — remove duplicate column refs in PRIMARY KEY
// ---------------------------------------------------------------------------
function dedupPrimaryKeys(tables) {
  const changes = []

  const result = tables.map(table => {
    if (!table.primaryKey) return table

    const seen = new Set()
    const dedupedCols = []
    let removed = 0

    for (const col of table.primaryKey.columns) {
      const key = col.toLowerCase()
      if (seen.has(key)) {
        removed++
      } else {
        seen.add(key)
        dedupedCols.push(col)
      }
    }

    if (removed > 0) {
      changes.push({
        type: 'dedup_pk',
        table: table.name,
        before: table.primaryKey.columns.length,
        after: dedupedCols.length,
        removed,
        message: `${table.name}: PK ${table.primaryKey.columns.length} → ${dedupedCols.length} columns (removed ${removed} duplicate refs)`,
      })
    }

    return {
      ...table,
      primaryKey: { ...table.primaryKey, columns: dedupedCols },
    }
  })

  return { tables: result, changes }
}

// ---------------------------------------------------------------------------
// Step 3: Fix types — VARCHAR(255) from PIC comments, bad DECIMAL precision
// ---------------------------------------------------------------------------
function fixTypes(tables) {
  const changes = []
  let varcharFixes = 0
  let decimalFixes = 0

  const result = tables.map(table => {
    const fixedCols = table.columns.map(col => {
      const fixed = { ...col }

      // Fix VARCHAR(255) → actual PIC length
      if (/^VARCHAR\(255\)$/i.test(col.type) && col.pic) {
        if (col.pic.type === 'alpha' && col.pic.length > 0) {
          fixed.type = `VARCHAR(${col.pic.length})`
          fixed.typeFixed = true
          varcharFixes++
        }
      }

      // Fix bad DECIMAL precision from PIC comment
      // e.g. DECIMAL(1006, 999) with -- PIC S9(7)V9999 → DECIMAL(11, 4)
      const decMatch = col.type.match(/^DECIMAL\((\d+),?\s*(\d+)\)$/i)
      if (decMatch) {
        const prec = parseInt(decMatch[1])
        const scale = parseInt(decMatch[2])

        // If precision > 31 (DB2 max) or scale > precision, it's bad
        if (prec > 31 || scale > prec) {
          if (col.pic && col.pic.type === 'decimal') {
            const newPrec = col.pic.intDigits + col.pic.decDigits
            const newScale = col.pic.decDigits
            fixed.type = `DECIMAL(${newPrec}, ${newScale})`
            fixed.typeFixed = true
            fixed.originalType = col.type
            decimalFixes++
          } else {
            // Can't auto-fix, flag as action item (handled in validate step)
            fixed.hasIssue = true
            fixed.issue = `DECIMAL(${prec}, ${scale}) invalid precision — no PIC comment to auto-fix`
          }
        }
      }

      return fixed
    })

    return { ...table, columns: fixedCols }
  })

  if (varcharFixes > 0) {
    changes.push({
      type: 'fix_varchar',
      count: varcharFixes,
      message: `Fixed ${varcharFixes} VARCHAR(255) → actual PIC lengths`,
    })
  }
  if (decimalFixes > 0) {
    changes.push({
      type: 'fix_decimal',
      count: decimalFixes,
      message: `Fixed ${decimalFixes} invalid DECIMAL precision from PIC comments`,
    })
  }

  return { tables: result, changes }
}

// ---------------------------------------------------------------------------
// Step 4: Dialect conversion — identifier quoting, type remaps, function remaps
// ---------------------------------------------------------------------------

const TYPE_REMAPS = {
  sqlserver_to_db2: {
    'NVARCHAR': 'VARCHAR',
    'NCHAR': 'CHAR',
    'NTEXT': 'CLOB',
    'BIT': 'SMALLINT',
    'DATETIME': 'TIMESTAMP',
    'DATETIME2': 'TIMESTAMP',
    'MONEY': 'DECIMAL(19,4)',
    'SMALLMONEY': 'DECIMAL(10,4)',
    'TINYINT': 'SMALLINT',
    'UNIQUEIDENTIFIER': 'CHAR(36)',
    'IMAGE': 'BLOB',
    'VARBINARY(MAX)': 'BLOB',
    'VARCHAR(MAX)': 'CLOB',
    'NVARCHAR(MAX)': 'CLOB',
  },
  sqlserver_to_postgresql: {
    'NVARCHAR': 'VARCHAR',
    'NCHAR': 'CHAR',
    'NTEXT': 'TEXT',
    'BIT': 'BOOLEAN',
    'DATETIME': 'TIMESTAMP',
    'DATETIME2': 'TIMESTAMP',
    'MONEY': 'NUMERIC(19,4)',
    'SMALLMONEY': 'NUMERIC(10,4)',
    'TINYINT': 'SMALLINT',
    'UNIQUEIDENTIFIER': 'UUID',
    'IMAGE': 'BYTEA',
  },
  db2_to_sqlserver: {
    'CLOB': 'NVARCHAR(MAX)',
    'BLOB': 'VARBINARY(MAX)',
    'TIMESTAMP': 'DATETIME2',
  },
  db2_to_postgresql: {
    'CLOB': 'TEXT',
    'BLOB': 'BYTEA',
  },
}

// Identity/sequence remaps
const IDENTITY_PATTERNS = {
  sqlserver: /\bIDENTITY\s*\(\s*\d+\s*,\s*\d+\s*\)/gi,
  db2_replacement: 'GENERATED ALWAYS AS IDENTITY',
  postgresql_replacement: '', // use SERIAL type instead
}

function remapType(colType, sourceDialect, targetDialect) {
  const key = `${sourceDialect}_to_${targetDialect}`
  const remaps = TYPE_REMAPS[key]
  if (!remaps) return colType

  // Check for exact match first (e.g. "VARBINARY(MAX)")
  const upper = colType.toUpperCase()
  if (remaps[upper]) return remaps[upper]

  // Check for base type match (e.g. "NVARCHAR(50)" → remap NVARCHAR → VARCHAR, keep (50))
  const baseMatch = colType.match(/^(\w+)(\(.+\))?$/i)
  if (baseMatch) {
    const base = baseMatch[1].toUpperCase()
    const args = baseMatch[2] || ''
    if (remaps[base]) {
      // If the remap already includes args (e.g. MONEY → DECIMAL(19,4)), use it directly
      if (remaps[base].includes('(')) return remaps[base]
      return remaps[base] + args
    }
  }

  return colType
}

function convertIdentifierQuoting(name, sourceDialect, targetDialect) {
  // Strip any existing quoting
  let bare = name.replace(/^["'`\[\]]/, '').replace(/["'`\]\]]$/, '')

  // Apply target quoting
  switch (targetDialect) {
    case 'sqlserver': return `[${bare}]`
    case 'mysql': return `\`${bare}\``
    case 'db2':
    case 'postgresql':
    case 'oracle':
    default: return `"${bare}"`
  }
}

function dialectConvert(tables, procedures, sourceDialect, targetDialect) {
  const changes = []

  if (sourceDialect === targetDialect) {
    return { tables, procedures, changes }
  }

  let tableConversions = 0
  let procConversions = 0

  // Convert tables
  const convertedTables = tables.map(table => {
    const convertedName = convertIdentifierQuoting(table.name, sourceDialect, targetDialect)
    const convertedCols = table.columns.map(col => ({
      ...col,
      name: col.name, // keep bare name, quoting applied at DDL generation
      type: remapType(col.type, sourceDialect, targetDialect),
    }))

    const convertedPk = table.primaryKey ? {
      ...table.primaryKey,
      name: table.primaryKey.name,
    } : null

    tableConversions++

    return {
      ...table,
      name: table.name, // keep bare name
      columns: convertedCols,
      primaryKey: convertedPk,
      convertedName,
    }
  })

  // Convert procedures — remap DDL text directly
  const convertedProcs = procedures.map(proc => {
    let convertedDdl = proc.rawDdl

    // Remap identifier quoting in proc body
    if (sourceDialect === 'sqlserver' && targetDialect !== 'sqlserver') {
      convertedDdl = convertedDdl.replace(/\[([^\]]+)\]/g, (_, name) =>
        convertIdentifierQuoting(name, sourceDialect, targetDialect)
      )
    }
    if (sourceDialect !== 'sqlserver' && targetDialect === 'sqlserver') {
      convertedDdl = convertedDdl.replace(/"([^"]+)"/g, (_, name) => `[${name}]`)
    }

    // Function remaps
    if (sourceDialect === 'sqlserver') {
      convertedDdl = convertedDdl.replace(/\bGETDATE\s*\(\s*\)/gi, 'CURRENT_TIMESTAMP')
      convertedDdl = convertedDdl.replace(/\bISNULL\s*\(/gi, 'COALESCE(')
      convertedDdl = convertedDdl.replace(/\bLEN\s*\(/gi, 'LENGTH(')
    }

    procConversions++

    return {
      ...proc,
      convertedDdl,
    }
  })

  if (tableConversions > 0) {
    changes.push({
      type: 'dialect_convert',
      count: tableConversions,
      message: `Converted ${tableConversions} tables from ${sourceDialect} → ${targetDialect}`,
    })
  }
  if (procConversions > 0) {
    changes.push({
      type: 'dialect_convert_procs',
      count: procConversions,
      message: `Converted ${procConversions} procedures from ${sourceDialect} → ${targetDialect}`,
    })
  }

  return { tables: convertedTables, procedures: convertedProcs, changes }
}

// ---------------------------------------------------------------------------
// Step 5: Validate — check for remaining issues, build action items
// ---------------------------------------------------------------------------
function validate(tables, procedures) {
  const actionItems = []

  for (const table of tables) {
    for (const col of table.columns) {
      // Check for still-bad DECIMAL
      const decMatch = col.type.match(/^DECIMAL\((\d+),?\s*(\d+)\)$/i)
      if (decMatch) {
        const prec = parseInt(decMatch[1])
        const scale = parseInt(decMatch[2])
        if (prec > 31 || scale > prec) {
          actionItems.push({
            objectType: 'table',
            object: table.name,
            column: col.name,
            issue: `DECIMAL(${prec}, ${scale}) invalid precision`,
            severity: 'error',
            resolution: col.pic
              ? 'Auto-fix from PIC comment'
              : 'Manual fix required — no PIC comment available',
            picComment: col.picComment || null,
          })
        }
      }

      // Flag columns that still have issues
      if (col.hasIssue) {
        actionItems.push({
          objectType: 'table',
          object: table.name,
          column: col.name,
          issue: col.issue,
          severity: 'error',
          resolution: 'Manual fix required',
        })
      }
    }
  }

  return { actionItems }
}

// ---------------------------------------------------------------------------
// Generate DDL for a single table
// ---------------------------------------------------------------------------
export function generateTableDdl(table, dialect) {
  const q = dialect === 'sqlserver' ? ['[', ']']
    : dialect === 'mysql' ? ['`', '`']
    : ['"', '"']

  const lines = []
  lines.push(`CREATE TABLE ${q[0]}${table.name}${q[1]} (`)

  for (let i = 0; i < table.columns.length; i++) {
    const col = table.columns[i]
    const nullStr = col.nullable ? '' : ' NOT NULL'
    const isLast = i === table.columns.length - 1 && !table.primaryKey
    const comma = isLast ? '' : ','
    lines.push(`    ${q[0]}${col.name}${q[1]} ${col.type}${nullStr}${comma}`)
  }

  if (table.primaryKey) {
    const pkCols = table.primaryKey.columns.map(c => `${q[0]}${c}${q[1]}`).join(', ')
    lines.push(`    CONSTRAINT ${q[0]}${table.primaryKey.name}${q[1]} PRIMARY KEY (${pkCols})`)
  }

  lines.push(');')

  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// Main conversion function
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Step 3b: Apply user-defined type mapping rules
// ---------------------------------------------------------------------------
function applyTypeMappingRules(tables, rules) {
  if (!rules || rules.length === 0) return { tables, changes: [] }

  const changes = []
  let fixCount = 0

  const result = tables.map(table => {
    const fixedCols = table.columns.map(col => {
      const fixed = { ...col }
      for (const rule of rules) {
        // Normalize for comparison: strip spaces around comma
        const colTypeNorm = col.type.replace(/\s*/g, '').toUpperCase()
        const ruleSourceNorm = rule.sourceType.replace(/\s*/g, '').toUpperCase()

        if (colTypeNorm === ruleSourceNorm) {
          // Check scope
          if (rule.scope && rule.scope !== 'all' && rule.scope.toUpperCase() !== table.name.toUpperCase()) {
            continue
          }
          fixed.type = rule.targetType
          fixed.typeFixed = true
          fixed.hasIssue = false // clear any previous issue
          fixed.issue = null
          fixCount++
          break // first matching rule wins
        }
      }
      return fixed
    })

    return { ...table, columns: fixedCols }
  })

  if (fixCount > 0) {
    changes.push({
      type: 'user_type_rules',
      count: fixCount,
      message: `Applied ${fixCount} user-defined type mapping fixes`,
    })
  }

  return { tables: result, changes }
}

export function convertSchema(parsedSchema, sourceDialect, targetDialect, selectedObjects = null, typeMappingRules = []) {
  // Filter by selected objects if provided (Set of "type:name" keys)
  const filterBySelection = (items, type) => {
    if (!selectedObjects) return items
    return items.filter(item => selectedObjects.has(`${type}:${item.name}`))
  }

  let tables = filterBySelection(parsedSchema.tables, 'table').map(t => ({ ...t, columns: t.columns.map(c => ({ ...c })), primaryKey: t.primaryKey ? { ...t.primaryKey, columns: [...t.primaryKey.columns] } : null }))
  let procedures = filterBySelection(parsedSchema.procedures, 'procedure').map(p => ({ ...p }))
  const views = filterBySelection(parsedSchema.views, 'view').map(v => ({ ...v }))
  const indexes = filterBySelection(parsedSchema.indexes, 'index').map(i => ({ ...i }))
  const allChanges = []

  // Step 1: Dedup columns
  const step1 = dedupColumns(tables)
  tables = step1.tables
  allChanges.push(...step1.changes)

  // Step 2: Dedup PK columns
  const step2 = dedupPrimaryKeys(tables)
  tables = step2.tables
  allChanges.push(...step2.changes)

  // Step 3: Fix types
  const step3 = fixTypes(tables)
  tables = step3.tables
  allChanges.push(...step3.changes)

  // Step 3b: Apply user-defined type mapping rules
  const step3b = applyTypeMappingRules(tables, typeMappingRules)
  tables = step3b.tables
  allChanges.push(...step3b.changes)

  // Step 4: Dialect convert
  const step4 = dialectConvert(tables, procedures, sourceDialect, targetDialect)
  tables = step4.tables
  procedures = step4.procedures
  allChanges.push(...step4.changes)

  // Step 5: Validate
  const { actionItems } = validate(tables, procedures)

  // Generate converted DDL for each table
  for (const table of tables) {
    table.convertedDdl = generateTableDdl(table, targetDialect)
  }

  // Build assessment
  const totalObjects = tables.length + procedures.length + views.length + indexes.length
  const issueObjects = new Set(actionItems.map(a => a.object))
  const autoConverted = totalObjects - issueObjects.size

  const assessment = {
    totalObjects,
    autoConverted,
    autoConvertedPct: totalObjects > 0 ? Math.round((autoConverted / totalObjects) * 100) : 0,
    manualIntervention: issueObjects.size,
    actionItems,
    changes: allChanges,
    summary: {
      tables: tables.length,
      procedures: procedures.length,
      views: views.length,
      indexes: indexes.length,
    },
  }

  return {
    tables,
    procedures,
    views,
    indexes,
    assessment,
    sourceDialect,
    targetDialect,
  }
}
